import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { Stage, GreenScreen, Truss, Technocrane, VideoVillage, CostumeRacks, StuntZone, PressBoard, Corridor, CinemaRoom, SetDressing, WallStills, FloorProps } from "./SetPieces";
import { SandSet, PropDressing } from "./PropDressing";
import { Crew } from "./Humans";
import { CinemaScreen3D } from "./CinemaScreen";
import { PhysicsProps } from "./PhysicsProps";

function CameraBeacon() {
  useFrame(({ camera }) => { window.__camPos = camera.position; });
  return null;
}

export const HOTSPOTS = [
  { id: "equipment", position: [-0.4, 1.7, -4], route: "/noleggio-attrezzatura" },
  { id: "actors", position: [3, 1.9, -5.1], route: "/filmografia?ruolo=attore" },
  { id: "director", position: [5, 1.9, -0.5], route: "/filmografia?ruolo=regista" },
  { id: "costumes", position: [10, 1.7, 2], route: "/noleggio-costumi" },
  { id: "gallery", position: [7, 1.9, 4.5], route: "/galleria" },
  { id: "stunt", position: [-10, 1.5, -3], route: "/stunt" },
  { id: "production", position: [22, 1.8, -4], route: "/produzioni" },
  { id: "about", position: [-16, 2.1, 2], route: "/chi-sono" },
  { id: "cinema", position: [38, 2.2, -0.5], route: "/proiezione" },
];

function isWalkable(x, z) {
  return (
    (x > -17.8 && x < 18.6 && z > -10.6 && z < 8.6) ||
    (x >= 18.6 && x < 25.4 && z > -5.7 && z < -2.3) ||
    (x >= 25.4 && x < 43.6 && z > -12.4 && z < 4.2)
  );
}

function Hotspot({ spot, active }) {
  const ring = useRef();
  const mat = useRef();
  const light = useRef();
  useFrame(({ clock, camera }) => {
    // the floor ring only fades in when the visitor gets close
    const d = Math.hypot(camera.position.x - spot.position[0], camera.position.z - spot.position[2]);
    const k = THREE.MathUtils.clamp((7.5 - d) / 3.2, 0, 1);
    const pulse = 1 + 0.22 * Math.sin(clock.elapsedTime * 2.4);
    if (ring.current) ring.current.scale.set(pulse, pulse, pulse);
    if (mat.current) mat.current.opacity = 0.7 * k;
    if (light.current) light.current.intensity = (active ? 14 : 5) * k;
  });
  return (
    <group position={[spot.position[0], 0.06, spot.position[2]]}>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.55, 0.68, 40]} />
        <meshBasicMaterial ref={mat} color="#f59e0b" transparent opacity={0} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      <pointLight ref={light} color="#f59e0b" intensity={0} distance={3} position={[0, 0.5, 0]} />
    </group>
  );
}

function HotspotManager({ onActive }) {
  const v = useMemo(() => new THREE.Vector3(), []);
  const count = useRef(0);
  useFrame(({ camera }) => {
    if (++count.current % 8) return;
    let best = null;
    let bd = 7;
    HOTSPOTS.forEach((h) => {
      const d = camera.position.distanceTo(v.set(h.position[0], h.position[1], h.position[2]));
      if (d < bd) { bd = d; best = h.id; }
    });
    onActive(best);
  });
  return null;
}

function PostFX() {
  const { gl, scene, camera, size } = useThree();
  const composer = useMemo(() => {
    const c = new EffectComposer(gl);
    c.addPass(new RenderPass(scene, camera));
    c.addPass(new UnrealBloomPass(new THREE.Vector2(size.width, size.height), 0.14, 0.5, 0.86));
    c.addPass(new OutputPass());
    return c;
  }, [gl, scene, camera]);
  useEffect(() => {
    composer.setSize(size.width, size.height);
  }, [composer, size]);
  useFrame(() => composer.render(), 1);
  return null;
}

function OrbitCam() {
  const { camera, gl } = useThree();
  const controls = useRef(null);
  useEffect(() => {
    camera.position.set(14, 9, 14);
    const c = new OrbitControlsImpl(camera, gl.domElement);
    c.target.set(4, 1, -2);
    c.enableDamping = true;
    c.dampingFactor = 0.06;
    c.maxPolarAngle = Math.PI / 2.05;
    c.minDistance = 3;
    c.maxDistance = 40;
    controls.current = c;
    return () => { c.dispose(); controls.current = null; };
  }, [camera, gl]);
  useFrame(() => { if (controls.current) controls.current.update(); });
  return null;
}

function ExploreControls() {
  const { camera, gl } = useThree();
  const S = useRef({
    yaw: 0, pitch: 0, yawT: 0, pitchT: 0,
    keys: {}, target: null, dragging: false,
    downX: 0, downY: 0, yaw0: 0, pitch0: 0, moved: 0,
  });

  useEffect(() => {
    camera.position.set(0, 1.7, 7.5);
    camera.quaternion.setFromEuler(new THREE.Euler(0, 0, 0, "YXZ"));
    const el = gl.domElement;
    const s = S.current;
    const raycaster = new THREE.Raycaster();
    const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const hitPoint = new THREE.Vector3();

    const onDown = (e) => {
      if (e.button !== 0) return;
      s.dragging = true; s.moved = 0;
      s.downX = e.clientX; s.downY = e.clientY;
      s.yaw0 = s.yawT; s.pitch0 = s.pitchT;
    };
    const onMove = (e) => {
      if (!s.dragging) return;
      const dx = e.clientX - s.downX;
      const dy = e.clientY - s.downY;
      s.moved = Math.max(s.moved, Math.abs(dx) + Math.abs(dy));
      s.yawT = s.yaw0 - dx * 0.0028;
      s.pitchT = THREE.MathUtils.clamp(s.pitch0 - dy * 0.0024, -0.55, 0.6);
    };
    const onUp = (e) => {
      const wasClick = s.moved < 6;
      s.dragging = false;
      if (!wasClick) return;
      const r = el.getBoundingClientRect();
      const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      if (raycaster.ray.intersectPlane(floorPlane, hitPoint)) {
        if (isWalkable(hitPoint.x, hitPoint.z)) s.target = { x: hitPoint.x, z: hitPoint.z };
      }
    };
    const dn = (e) => { s.keys[e.code] = true; };
    const up = (e) => { s.keys[e.code] = false; };
    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("keydown", dn);
    window.addEventListener("keyup", up);
    el.style.touchAction = "none";
    return () => {
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("keydown", dn);
      window.removeEventListener("keyup", up);
    };
  }, [camera, gl]);

  useFrame(({ clock }, delta) => {
    const s = S.current;
    s.yaw += (s.yawT - s.yaw) * 0.12;
    s.pitch += (s.pitchT - s.pitch) * 0.12;
    camera.quaternion.setFromEuler(new THREE.Euler(s.pitch, s.yaw, 0, "YXZ"));

    const k = s.keys;
    const speed = 4.2 * delta;
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    dir.y = 0;
    dir.normalize();
    const right = new THREE.Vector3().crossVectors(dir, new THREE.Vector3(0, 1, 0));
    const move = new THREE.Vector3();
    if (k.KeyW || k.ArrowUp) move.add(dir);
    if (k.KeyS || k.ArrowDown) move.sub(dir);
    if (k.KeyA || k.ArrowLeft) move.sub(right);
    if (k.KeyD || k.ArrowRight) move.add(right);

    let moving = false;
    if (move.lengthSq() > 0) {
      move.normalize().multiplyScalar(speed);
      s.target = null;
      moving = true;
    } else if (s.target) {
      const dx = s.target.x - camera.position.x;
      const dz = s.target.z - camera.position.z;
      const d = Math.hypot(dx, dz);
      if (d < 0.25) s.target = null;
      else {
        move.set(dx / d, 0, dz / d).multiplyScalar(Math.min(3.4 * delta, d));
        moving = true;
      }
    }
    if (move.lengthSq() > 0) {
      const nx = camera.position.x + move.x;
      const nz = camera.position.z + move.z;
      if (isWalkable(nx, camera.position.z)) camera.position.x = nx;
      if (isWalkable(camera.position.x, nz)) camera.position.z = nz;
    }
    const t = clock.elapsedTime;
    camera.position.y = moving ? 1.7 + Math.sin(t * 9.5) * 0.032 : 1.7;
  });
  return null;
}

export function StudioCanvas({ mode, activeId, onActive, audioOn }) {
  return (
    <Canvas
      shadows
      dpr={[1, 1.75]}
      camera={{ fov: 62, position: [0, 1.7, 7.5], near: 0.1, far: 90 }}
      gl={{ antialias: true }}
      data-testid="studio-canvas"
      onCreated={({ gl, scene }) => {
        const pmrem = new THREE.PMREMGenerator(gl);
        scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.06).texture;
      }}
    >
      <color attach="background" args={["#07080a"]} />
      <fog attach="fog" args={["#07080a", 30, 65]} />
      <hemisphereLight args={["#9db4d0", "#1a120c", 0.55]} />
      <ambientLight intensity={0.22} />
      <Stage />
      <GreenScreen />
      <Truss />
      <Technocrane />
      <VideoVillage />
      <CostumeRacks />
      <StuntZone />
      <PressBoard />
      <Corridor />
      <CinemaRoom />
      <SetDressing />
      <FloorProps />
      <SandSet />
      <PropDressing />
      <WallStills />
      <Crew />
      <PhysicsProps />
      <CameraBeacon />
      <CinemaScreen3D audioOn={audioOn} />
      {HOTSPOTS.map((h) => (
        <Hotspot key={h.id} spot={h} active={activeId === h.id} />
      ))}
      <HotspotManager onActive={onActive} />
      {mode === "walk" ? <ExploreControls /> : <OrbitCam />}
      <PostFX />
    </Canvas>
  );
}
